import { createContext, useContext, useState, useCallback } from 'react'

/**
 * LoadingContext - Global loading state management
 * 
 * Provides centralized control for background activity indicators.
 * Any component can trigger loading state, and the UI (e.g., logo) responds.
 * 
 * Features:
 * - Track multiple concurrent loading operations
 * - Automatic cleanup with unique IDs
 * - Single source of truth for loading state
 * 
 * @atomic - Single responsibility: manage loading state
 * @modular - Can be used by any component
 * @isolated - State changes don't affect component tree structure
 */
const LoadingContext = createContext()

export function LoadingProvider({ children }) {
  // Track active loading operations by unique ID
  const [loadingOperations, setLoadingOperations] = useState(new Set())

  /**
   * Start a loading operation
   * @param {string} operationId - Unique identifier for the operation
   */
  const startLoading = useCallback((operationId = 'default') => {
    setLoadingOperations(prev => new Set([...prev, operationId]))
  }, [])

  /**
   * End a loading operation
   * @param {string} operationId - Unique identifier for the operation
   */
  const stopLoading = useCallback((operationId = 'default') => {
    setLoadingOperations(prev => {
      const next = new Set(prev)
      next.delete(operationId)
      return next
    })
  }, [])

  /**
   * Check if any operations are loading
   * @returns {boolean}
   */
  const isLoading = loadingOperations.size > 0

  const value = {
    isLoading,
    startLoading,
    stopLoading,
    loadingCount: loadingOperations.size
  }

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  )
}

/**
 * Hook to access loading state
 * @returns {Object} { isLoading, startLoading, stopLoading, loadingCount }
 */
export function useLoading() {
  const context = useContext(LoadingContext)
  if (!context) {
    throw new Error('useLoading must be used within LoadingProvider')
  }
  return context
}
